import React, { useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { crmFormsApi } from '../../services/api';
import { apiErrorMessage } from '../../utils/forms';

const LIMIT = 20;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const show = (v) => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v));

export default function ResponsesTab({ formId, slug }) {
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [openId, setOpenId] = useState(null);

  const { data, isLoading, error, isFetching } = useQuery(
    ['crm-form-submissions', formId, page],
    () => crmFormsApi.getSubmissions(formId, { page, limit: LIMIT }),
    { keepPreviousData: true }
  );
  const rows = data?.data?.submissions || [];
  const pg = data?.data?.pagination || { page: 1, totalPages: 1, total: 0 };

  const exportCsv = async () => {
    setExporting(true);
    try { await crmFormsApi.exportCsv(formId, slug); } catch (err) { toast.error(err.message || 'Erro ao exportar'); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">{pg.total} resposta{pg.total === 1 ? '' : 's'}</p>
        <button type="button" className="btn btn-secondary flex items-center gap-2" onClick={exportCsv} disabled={exporting || pg.total === 0}>
          <span className="material-symbols-outlined text-[18px]">download</span>
          {exporting ? 'Exportando...' : 'Exportar CSV'}
        </button>
      </div>

      {isLoading && <p className="text-slate-500">Carregando...</p>}
      {error && <p className="text-red-600">{apiErrorMessage(error, 'Erro ao carregar respostas')}</p>}
      {!isLoading && !error && rows.length === 0 && <div className="card p-8 text-center text-slate-500">Nenhuma resposta ainda.</div>}

      {rows.length > 0 && (
        <div className={`card overflow-x-auto ${isFetching ? 'opacity-70' : ''}`}>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="p-3">Data</th><th className="p-3">Nome</th><th className="p-3">Telefone</th><th className="p-3">E-mail</th><th className="p-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                    <td className="p-3 whitespace-nowrap">{fmt(r.createdAt)}</td>
                    <td className="p-3">{r.contactName || '—'}</td>
                    <td className="p-3 whitespace-nowrap">{r.contactPhone || '—'}</td>
                    <td className="p-3 break-all">{r.contactEmail || '—'}</td>
                    <td className="p-3">
                      <span className="material-symbols-outlined text-[18px] text-slate-400">{openId === r.id ? 'expand_less' : 'expand_more'}</span>
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr className="bg-surface-container-low">
                      <td colSpan={5} className="p-4">
                        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                          {(Array.isArray(r.answers) ? r.answers : []).map((a, i) => (
                            <div key={a.questionId || i}>
                              <dt className="text-xs text-slate-500">{a.label || a.questionId}</dt>
                              <dd className="font-medium break-words">{show(a.value) || '—'}</dd>
                            </div>
                          ))}
                        </dl>
                        {r.routingOutcome && (
                          <p className="text-xs text-slate-500 mt-3">
                            Destino: funil {r.routingOutcome.pipelineId ?? '—'} · etapa {r.routingOutcome.stageId ?? '—'} · {r.routingOutcome.assigneeMode === 'fixed' ? 'responsável fixo' : 'rodízio'}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pg.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
          <span className="text-sm text-slate-600">Página {pg.page} de {pg.totalPages}</span>
          <button type="button" className="btn btn-secondary" disabled={page >= pg.totalPages} onClick={() => setPage((p) => p + 1)}>Próxima</button>
        </div>
      )}
    </div>
  );
}
